import React, { useState } from 'react';
import {
  SaveButton,
  DeleteButton,
  Toolbar,
  useSaveContext,
  useResourceContext,
  useRecordContext,
  SaveContextProvider,
} from 'react-admin';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Tooltip,
} from '@mui/material';
import { gql } from '@apollo/client';
import { useApolloClientContext } from 'providers/apolloClientContextProvider';
import useCheckAccess from 'hooks/useCheckAccess';
import useGate from 'hooks/useGate';
import { gateNames } from 'constants/statsig';

const CHECK_PENDING_REVIEWS_QUERY = gql`
  query autoGeneratedRates($input: AutoGeneratedRateQueryInput) {
    autoGeneratedRates(input: $input) {
      id
      status
    }
  }
`;

export const SaveWithConfirmationWrapper = ({ children }) => {
  const saveContext = useSaveContext();
  const resource = useResourceContext();
  const record = useRecordContext();
  const { client } = useApolloClientContext();
  const { value: isFeatureEnabled } = useGate(gateNames.basePayRateAutomation);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  const isMarketPositionRateSetting = resource === 'MarketPositionRateSetting';
  const shouldInterceptSave = isFeatureEnabled && isMarketPositionRateSetting && record?.id;

  const checkPendingReviews = async () => {
    try {
      setIsChecking(true);
      const { data } = await client.query({
        query: CHECK_PENDING_REVIEWS_QUERY,
        variables: {
          input: {
            search: {
              scope_id: record.market_id,
              position_id: record.position_id,
              status: 'pending_review',
            },
          },
        },
        fetchPolicy: 'network-only',
      });

      const hasPendingReviews = data?.autoGeneratedRates?.some(
        (rate: { status: string }) => rate.status === 'pending_review',
      );

      setIsChecking(false);
      return hasPendingReviews;
    } catch (error) {
      setIsChecking(false);
      return false;
    }
  };

  const handleSave = async (values: Record<string, unknown>) => {
    if (!shouldInterceptSave) {
      return saveContext?.save?.(values);
    }

    const hasPendingReviews = await checkPendingReviews();
    if (hasPendingReviews) {
      setIsModalOpen(true);
      // Return a resolved promise that does nothing to prevent save
      // This avoids infinite loading state
      return Promise.resolve();
    }

    return saveContext?.save?.(values);
  };

  if (!shouldInterceptSave) {
    return <>{children}</>;
  }

  return (
    <SaveContextProvider
      value={{
        save: handleSave,
        saving: isChecking || saveContext?.saving,
        mutationMode: saveContext?.mutationMode || 'pessimistic',
      }}
    >
      {children}
      <SaveWithConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        record={record}
      />
    </SaveContextProvider>
  );
};

const SaveWithConfirmationModal = ({ isOpen, onClose, record }) => {
  const checkAccess = useCheckAccess();
  const canViewAutoGeneratedRates = checkAccess('view', 'AutoGeneratedRate');

  const handleGoToApprovalFlow = () => {
    const filterParams = {
      search: {
        scope_id: Number(record.market_id),
        status: 'pending_review',
      },
      position_id: Number(record.position_id),
    };
    const url = `${window.location.origin}/AutoGeneratedRate?filter=${encodeURIComponent(
      JSON.stringify(filterParams),
    )}`;
    window.open(url, '_blank');
    onClose();
  };

  return (
    <Dialog open={isOpen} maxWidth="sm" fullWidth onClose={onClose}>
      <DialogTitle>Pending Review Required</DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          You cannot modify this rate because there is already an auto-generated record waiting for
          review. Please approve or reject the pending item in the Approval Flow first
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {canViewAutoGeneratedRates ? (
          <Button onClick={handleGoToApprovalFlow} variant="contained" color="primary">
            Go to Approval Flow
          </Button>
        ) : (
          <Tooltip title="You don't have permission to view the Auto Approval Rates screen.">
            <span>
              <Button disabled variant="contained" color="primary">
                Go to Approval Flow
              </Button>
            </span>
          </Tooltip>
        )}
      </DialogActions>
    </Dialog>
  );
};

const SaveWithConfirmationToolbar = () => {
  return (
    <Toolbar style={{ display: 'flex', justifyContent: 'space-between' }}>
      <SaveButton />
      <DeleteButton />
    </Toolbar>
  );
};

export default SaveWithConfirmationToolbar;
